import { Callout, DirectionalHint, IButtonProps, IconButton, Text, TooltipHost } from "@fluentui/react";
import { IComparable, IFlexibleFieldValueMerged } from "global/Compare/ComparisonEngine";
import { EMergeAction_DiagnosisLabels, EMergeVMStatus, IPropertyValueVM } from "global/Compare/ComparisonViewModelGenerator";
import { BuildHighlightedText } from "global/Utility/TSFunctions";
import { ECommentStatus, IComment } from "model/FlexibleAttributeModel";
import { ReactNode, useCallback, useState } from "react";
import { TABLE_STYLES } from "../Pharmacy/PharmacyPlan/PharmacyPlan.constants";
import { IEntityComparePanelProps } from "./EntityComparePanel";
import FieldMergePicker from "./FieldMergePicker";

const PRIMARY_PCP_SPECIALIST_MAP: Record<string, string> = {
    "primary": "Primary",
    "pcp": "PCP",
    "specialist": "Specialist",
}

const FILTER_ON_STYLES = {
    backgroundColor: 'transparent',
    transition: 'background-color 0.5s',
    // filter: 'blur(3px) brightness(0.8)',
    // transition: 'filter 0.5s',
}

const FILTER_OFF_STYLES = {
    backgroundColor: '#33ff0047',
    transition: 'background-color 0.5s',
    // filter: 'blur(0px)',
    // transition: 'filter 0.5s',
}

enum EDisplaySource {
    Origin,
    Target,
}

const markerStyle: React.CSSProperties = { display: 'inline' }
const changeStyle: React.CSSProperties = { backgroundColor: '#fbff04' }
const comment_STYLES: IButtonProps['styles'] = {
    root: { padding: 0, margin: 0, width: '12px', height: '12px' },
    icon: { fontSize: '12px' }
}

const commentCallout_STYLES = {
    calloutMain: {
        minWidth: 520,
        maxWidth: 760,
        padding: 10,
    }
};

const commentPreviewText_STYLES = {
    root: {
        fontSize: '16px',
        lineHeight: '24px',
    }
};

const actionColStyleByCommentStatus: Record<ECommentStatus, React.CSSProperties> = {
    [ECommentStatus.New]: {
        backgroundColor: '#ffe066',
        borderLeft: '4px solid #ff8f00',
        borderTop: '1px solid #b8a900c1',
    },
    [ECommentStatus.Resolved]: {
        backgroundColor: '#8ce8a8',
        borderLeft: '4px solid #1e8e3e',
        borderTop: '1px solid #b8a900c1',
        opacity: 0.85,
    },
};

export const buildLabel = (
    bcbs_label: string,
    mergedValue: IComparable<IFlexibleFieldValueMerged, undefined>
) => {
    const tier = mergedValue.onTarget?.tier ?? mergedValue.onOrigin?.tier;

    let returnLabel: string | undefined = bcbs_label;

    if (tier) {

        const tierName = tier.bcbs_name ?? tier.name ?? '';

        const type = mergedValue.onTarget?.value?.type ?? mergedValue.onOrigin?.value?.type;

        if (type !== "primary") {
            const typeLabel = PRIMARY_PCP_SPECIALIST_MAP[type ?? ''] ?? (type ?? '');
            returnLabel = returnLabel + ` - ${tierName} - ${typeLabel}`;
        } else {
            returnLabel = returnLabel + ` - ${tierName}`;
        }
    }

    return returnLabel;
};
/**
 * Props for the CompareRow component.
 */
interface ICompareRowProps {

    // The name of the field being compared.
    name: ReactNode;

    // Callback function to handle comment click events. (underwriting app only)
    onComment?: () => void;

    // Existing row comment to display on hover when available.
    comment?: IComment;

    // The value of the field in the origin.
    valueVM: Pick<IPropertyValueVM, 'mergeAction' | 'changed' | 'origin' | 'target'>;

    setFieldMergeAction?: (mergeAction: EMergeVMStatus) => void;

    entityProps: Pick<IEntityComparePanelProps, 'isUnderwritingApp' | 'isMergeApp'>;

    'data-property'?: string;
    'data-config-attribute-id'?: string;
    'data-config-master-attribute-id'?: string;
    'data-value-origin-id'?: string;
    'data-value-target-id'?: string;
}

export const CompareRow: React.FC<ICompareRowProps> = (props) => {

    const [highlightDiff, setHighlightDiff] = useState(false);
    const [showCommentPreview, setShowCommentPreview] = useState(false);
    const [commentPreviewButtonId] = useState(() => `comment-preview-${Math.random().toString(36).slice(2)}`);

    const mergeAction = props.valueVM.mergeAction;

    const [_, setFieldMergeAction] = useState<EMergeVMStatus | null>(mergeAction ?? null);

    const onClickHighlightChanges = useCallback(() => {
        setHighlightDiff(prev => !prev);
    }, [setHighlightDiff]);

    const highlightOrigin = (
        mergeAction === EMergeVMStatus.UseOrigin ||
        mergeAction === EMergeVMStatus.ParentOverrideUseOrigin
    );

    const highlightTarget = (mergeAction === EMergeVMStatus.UseTarget ||
        mergeAction === EMergeVMStatus.ParentOverrideUseTarget
    );

    const hideFieldMergePicker = !props.entityProps.isMergeApp ||
        mergeAction === EMergeVMStatus.Disabled ||
        mergeAction === EMergeVMStatus.ParentOverride ||
        mergeAction === EMergeVMStatus.ParentOverrideUseOrigin ||
        mergeAction === EMergeVMStatus.ParentOverrideUseTarget;

    const diagnosisView = false; // TODO: determine based on context

    const originValue = props.valueVM.origin ?? '---';
    const targetValue = props.valueVM.target ?? '---';
    const commentText = props.comment?.comment ?? '';
    const hasComment = commentText.trim().length > 0;



    const renderValue = (value: string, side: EDisplaySource, isMarked: boolean) => {
        if (!highlightDiff || originValue === targetValue) {
            return (
                <div style={(highlightTarget || highlightOrigin)
                    ? (!isMarked ? FILTER_ON_STYLES : FILTER_OFF_STYLES)
                    : undefined
                }>
                    <Text>
                        {isMarked
                            ? <div style={markerStyle}>✔️</div>
                            : undefined
                        }
                        {value}
                    </Text>
                </div>
            )
        }

        const diff = BuildHighlightedText(originValue, targetValue);

        const prefix = side === EDisplaySource.Origin ? diff.originPrefix : diff.targetPrefix;
        const changed = side === EDisplaySource.Origin ? diff.originDiff : diff.targetDiff;
        const suffix = side === EDisplaySource.Origin ? diff.originSuffix : diff.targetSuffix;

        return (
            <div style={(highlightTarget || highlightOrigin)
                ? FILTER_OFF_STYLES
                : undefined
            }>
                <Text>
                    {isMarked
                        ? <div style={markerStyle}>✔️</div>
                        : undefined
                    }
                    <span>
                        {prefix}
                    </span>
                    <span style={changeStyle}>
                        {changed || ' '}
                    </span>
                    <span>
                        {suffix}
                    </span>
                </Text>
            </div>

        );
    };

    const onCommentClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();

        props.onComment?.();
    }, [props.onComment]);

    return (
        <tr
            className={props.valueVM.changed ? 'highlight row-select-anchor' : undefined}
            onMouseLeave={() => {
                setShowCommentPreview(false);
            }}
            data-property={props['data-property']}
            data-config-attribute-id={props['data-config-attribute-id']}
            data-config-master-attribute-id={props['data-config-master-attribute-id']}
            data-value-origin-id={props['data-value-origin-id']}
            data-value-target-id={props['data-value-target-id']}
        >
            <td style={TABLE_STYLES.tbodyColName}>
                <Text styles={TABLE_STYLES.headerStyles}>
                    {props.name}
                    {diagnosisView && (
                        <Text variant='xSmall' styles={{ root: { fontStyle: 'italic' } }}>
                            ⚠️{EMergeAction_DiagnosisLabels[mergeAction]}
                        </Text>
                    )}
                </Text>
            </td>

            <td style={TABLE_STYLES.tbodyColOrigin}
                onClick={onClickHighlightChanges}
            >
                <div>
                    {renderValue(originValue, EDisplaySource.Origin, highlightOrigin)}
                </div>
            </td>

            <td style={TABLE_STYLES.tbodyColTarget}
                onClick={onClickHighlightChanges}
            >
                <div>
                    {renderValue(targetValue, EDisplaySource.Target, highlightTarget)}
                </div>
            </td>

            {(props.entityProps.isMergeApp || props.entityProps.isUnderwritingApp) && (
                <td style={{
                    ...TABLE_STYLES.tbodyColAction,
                    ...(props.comment ? actionColStyleByCommentStatus[props.comment.status] : undefined),
                }}>
                    {props.valueVM && !hideFieldMergePicker && (
                        <FieldMergePicker
                            mergeAction={props.valueVM?.mergeAction}
                            onMergeActionChange={(mergeAction) => {
                                if (props.valueVM) {
                                    props.valueVM.mergeAction = mergeAction;
                                    setFieldMergeAction(mergeAction);
                                }
                            }}
                        />
                    )}
                    {props.onComment && props.entityProps.isUnderwritingApp && (
                        <TooltipHost content={hasComment ? 'Edit comment' : 'Add comment'}>
                            <IconButton
                                id={commentPreviewButtonId}
                                iconProps={{ iconName: hasComment ? 'CommentSolid' : 'CommentAdd' }}
                                title={hasComment ? 'Edit comment' : 'Add comment'}
                                ariaLabel={hasComment ? 'Edit comment' : 'Add comment'}
                                onClick={onCommentClick}
                                onMouseEnter={() => {
                                    if (hasComment) {
                                        setShowCommentPreview(true);
                                    }
                                }}
                                onMouseLeave={() => setShowCommentPreview(false)}
                                styles={comment_STYLES}
                            />
                        </TooltipHost>
                    )}

                    {props.onComment && props.entityProps.isUnderwritingApp && hasComment && showCommentPreview && (
                        <Callout
                            target={`#${commentPreviewButtonId}`}
                            directionalHint={DirectionalHint.rightCenter}
                            gapSpace={6}
                            setInitialFocus={false}
                            onDismiss={() => setShowCommentPreview(false)}
                            styles={commentCallout_STYLES}
                        >
                            <Text styles={commentPreviewText_STYLES}>{commentText}</Text>
                        </Callout>
                    )}

                </td>
            )}
        </tr>

    );
}

export default CompareRow;

